import { ChangeEvent, KeyboardEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';

const containsSearchText = (
  option: SelectSearchOption,
  searchText: string,
): boolean => {
  const { label, secondLabel } = option;
  const searchValue = searchText.toLowerCase();

  return (
    label.toLowerCase().includes(searchValue) ||
    secondLabel?.toLowerCase().includes(searchValue) ||
    false
  );
};

export type SelectSearchOption = {
  value: number | string;
  label: string;
  secondLabel?: string;
};

export type SelectSearchProps = {
  name: string;
  errorMessage?: string;
  selectOptions: Array<SelectSearchOption>;
  defaultMenuItems?: { value: string; label: string };
} & SelectProps;

export const SelectSearch = ({
  name,
  selectOptions,
  errorMessage,
  defaultMenuItems,
  ...rest
}: SelectSearchProps) => {
  const { t } = useTranslation();
  const [searchItemValue, setSearchItemValue] = useState('');
  const { errors, touched } = useFormikContext<any>();

  const getError = (key: string) =>
    !!errors[key] && touched[key] ? t(`validationList.${[errors[key]]}`) : '';
  const showError = getError(name).length > 0;

  const displayedOptions = useMemo(() => {
    return selectOptions.filter((option) =>
      containsSearchText(option, searchItemValue),
    );
  }, [searchItemValue, selectOptions]);

  const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };
  const onChangeTextFieldHandler = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchItemValue(e.target.value);

  const onCloseHandler = () => setSearchItemValue('');

  return (
    <FormControl fullWidth>
      <InputLabel id="search-select-label" error={showError}>
        {t(rest.placeholder as string)}
      </InputLabel>
      <Select
        {...rest}
        fullWidth
        label={t(rest.placeholder as string)}
        name={name}
        error={showError}
        id="search-select"
        labelId="search-select-label"
        onClose={onCloseHandler}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: '430px',
              '& .MuiMenuItem-root': {
                overflow: 'auto',
              },
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Поиск"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={onChangeTextFieldHandler}
            onKeyDown={onKeyDownHandler}
          />
        </ListSubheader>
        {defaultMenuItems && (
          <MenuItem value={defaultMenuItems.value} sx={{ display: 'none' }}>
            <Typography color={(theme) => theme.palette.text.disabled}>
              {t(defaultMenuItems.label)}
            </Typography>
          </MenuItem>
        )}
        {displayedOptions.map(({ value, label, secondLabel }) => (
          <MenuItem key={value} value={value}>
            <Stack flexDirection={'row'} gap={1}>
              <Typography>{label}</Typography>
              {secondLabel && (
                <>
                  <Typography>/</Typography>
                  <Typography color={(theme) => theme.palette.text.disabled}>
                    {secondLabel}
                  </Typography>
                </>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error>{getError(name)}</FormHelperText>
    </FormControl>
  );
};
